import {
  faChevronLeft, faChevronRight,
  faSpinner, faUserPlus
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button, Card, Checkbox, FormControlLabel, Grid,
  Radio, Step, StepLabel, Stepper, TextField, Tooltip, Typography
} from '@mui/material';
import {
  CustomTooltip,
  Datepicker,
  FormAddress,
  FormCountryIdentifier,
  FormElement, RequiredSelectHack, Wrapper
} from 'components';
import { useFetch, useModal } from 'hooks';
import PropTypes from 'prop-types';
import React, {
  Fragment, useCallback, useEffect, useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  ApplicationRolesService, CountryService, DataTecneaService, UserService
} from 'services';
import { userStore } from 'stores';
import styled from 'styled-components';
import { translate } from 'utils';
import { APPLICATION_ROLES, ROUTES } from 'utils/constants';
import { FormHelper, UserHelper } from 'utils/helpers';
import { PurchaseOrderCard } from '../_commons/PurchaseOrderCard';

const FormContainer = styled(Card)`
  margin: 2rem auto;
  padding: 0 2rem 2rem 2rem;
`;

const FORM_STEPS = [
  {
    key: 0,
    label: 'inscriptionForm.yourInfos'
  }, {
    key: 1,
    label: 'inscriptionForm.institutionInfos'
  }, {
    key: 2,
    label: 'inscriptionForm.accessApp'
  }
];

const EXCLUSIVE_DATABILAN_ACTIVITIES = ['DATABILAN_OPERATOR', 'DATABILAN_DISTRIBUTOR'];

const LAST_STEP = 2;

export const RegisterFormComponent = ({
                                        isLoading, allApplicationRoles, onSubmit
                                      }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [activeApps, setActiveApps] = useState([]);
  const [, setRefreshCounter] = useState(0);
  const [accountsNeededFieldName, setAccountsNeededFieldName] = useState('');
  const [identifierFields, setIdentifierFields] = useState([]);
  const [databilanAdditionalFiles, setDatabilanAdditionalFiles] = useState([]);
  const [datafluidesAdditionalFiles, setDatafluidesAdditionalFiles] = useState([]);
  const [databilanPurchaseOrders, setDatabilanPurchaseOrders] = useState([]);
  const [datafluidesPurchaseOrders, setDatafluidesPurchaseOrders] = useState([]);

  const currentLanguage = localStorage.getItem('i18nextLng') || 'fr';
  const {
 control, handleSubmit, setValue, watch, formState: { errors, touchedFields }, trigger, setError
} = useForm({
    defaultValues: {
      validityDate: null,
      accessActivity: [],
      databilanAccountLimit: 1,
      dataformAccountLimit: 1,
      legalStatus: null,
      country: UserHelper.getDefaultCountry(null)
    }
  });


  const formValues = watch();

  useEffect(() => {
    DataTecneaService.getActiveApplications()
        .then(resp => setActiveApps(resp));

    CountryService.getCountrySelectItems().then(resp => {
      setValue('country', resp.find(lg => lg.value === currentLanguage));
    });
    // eslint-disable-next-line
  }, [currentLanguage]);

  const canAdministrate = useCallback(() => UserHelper.hasAccessRight([APPLICATION_ROLES.ADMIN]), []);

  const handleChangeValidityDate = useCallback(date => setValue('validityDate', date), [setValue]);

  const onFormSubmit = useCallback((data) => {
    onSubmit({
      ...data,
      identifiers: identifierFields,
      address: {
        address1: data.address1,
        address2: data.address2,
        postalCode: data.postalCode,
        city: data.city,
        country: {
          ...data.country,
          key: data.country && data.country.value
        }
      },
      purchaseOrders: { DATAFLUIDES: datafluidesPurchaseOrders[0]?.document, DATABILAN: databilanPurchaseOrders[0]?.document },
      additionalFiles: { DATAFLUIDES: { documents: datafluidesAdditionalFiles.map(doc => doc.document) }, DATABILAN: { documents: databilanAdditionalFiles.map(doc => doc.document) } }
    });
  }, [onSubmit, identifierFields, datafluidesPurchaseOrders, databilanPurchaseOrders, datafluidesAdditionalFiles, databilanAdditionalFiles]);

  const goToNextStep = useCallback(() => setActiveStep(activeStep + 1), [activeStep]);

  const checkErrorsForActiveStep = useCallback(async () => {
    switch (activeStep) {
      case 0:
        await trigger(['lastName', 'firstName', 'email', 'login']);
        break;
      case 1:
        await trigger(['country', 'identifiers', 'identifierSuffix', 'legalStatus', 'companyName',
          'institutionName', 'name', 'postalCode', 'city', 'address1']);
        break;
      default:
        break;
    }
    setRefreshCounter(refreshC => refreshC + 1);
  }, [activeStep, trigger]);

  const handleNextStep = useCallback(async () => {
    const formIsNativelyValid = document.forms.registerForm.reportValidity();
    const emailHasError = errors.email;
    const passwordHasError = errors.password;
    await checkErrorsForActiveStep();
    const isCurrentStepValid = formIsNativelyValid && !emailHasError && !passwordHasError;
    if (isCurrentStepValid) {
      if (activeStep === LAST_STEP) {
        handleSubmit(onFormSubmit)();
        return;
      }
      goToNextStep();
    }

    return null;
  }, [checkErrorsForActiveStep, errors, activeStep, handleSubmit, onFormSubmit, goToNextStep]);

  const switchName = useCallback(() => {
    if (formValues.accessActivity?.some(activity => activity.includes('DATABILAN'))) {
      setAccountsNeededFieldName('databilanAccountLimit');
    } else if (formValues.accessActivity?.includes('DATAFORM')) {
      setAccountsNeededFieldName('dataformAccountLimit');
    } else {
      setAccountsNeededFieldName('');
    }
  }, [formValues.accessActivity]);

  useEffect(() => {
    switchName();
  }, [switchName, formValues.accessActivity]);

  const handleBackStep = useCallback(() => setActiveStep(activeStep - 1), [activeStep]);

  const getFilesByActivity = useCallback((activity) => {
    if (activity.includes('DATAFLUIDES')) {
      return {
        purchaseOrderFiles: datafluidesPurchaseOrders,
        setPurchaseOrderFiles: setDatafluidesPurchaseOrders,
        additionalFiles: datafluidesAdditionalFiles,
        setAdditionalFiles: setDatafluidesAdditionalFiles
      };
    } if (activity.includes('DATABILAN')) {
      return {
        additionalFiles: databilanAdditionalFiles,
        setAdditionalFiles: setDatabilanAdditionalFiles,
        purchaseOrderFiles: databilanPurchaseOrders,
        setPurchaseOrderFiles: setDatabilanPurchaseOrders
      };
    }
    return null;
  }, [databilanAdditionalFiles, databilanPurchaseOrders, datafluidesAdditionalFiles, datafluidesPurchaseOrders]);

  const renderPurchaseOrderForms = useCallback(() => (
      <Grid container direction="row" justifyContent="space-around" style={{ maxWidth: '100%' }} wrap="nowrap">
        {formValues.accessActivity?.map((activity) => (
            <PurchaseOrderCard
                accountsNeededFieldName={accountsNeededFieldName}
                accountsNeededFieldNameProps={name => ({
                  value: formValues[name] || 1,
                  onChange: e => {
                    const value = typeof e === 'object' && e?.target ?
                        Number(e.target.value) :
                        Number(e);
                    setValue(name, value);
                  },
                  onBlur: () => trigger(name)
                })}
                activity={activity}
                key={activity}
                {...getFilesByActivity(activity)}
                title={activeApps.find(app => app.value === activity)?.label}
                xs={formValues.accessActivity ? 12 / formValues.accessActivity.length : 12}
            />
        ))}
      </Grid>
  ), [formValues.accessActivity, accountsNeededFieldName, formValues, setValue, trigger, getFilesByActivity, activeApps]);
  const checkExclusivity = useCallback((e) => {
    const newActivity = e.target.value;
    const currentActivities = formValues.accessActivity || [];

    if (currentActivities.includes(newActivity)) {
      // Remove the activity
      setValue('accessActivity', currentActivities.filter(activity => activity !== newActivity));
    } else {
      let activitiesWithoutExclusivity;
      if (EXCLUSIVE_DATABILAN_ACTIVITIES.includes(newActivity)) {
        activitiesWithoutExclusivity = currentActivities.filter(activity => !EXCLUSIVE_DATABILAN_ACTIVITIES.includes(activity));
        setDatabilanAdditionalFiles([]);
        setDatabilanPurchaseOrders([]);
      } else {
        activitiesWithoutExclusivity = currentActivities;
        setDatafluidesAdditionalFiles([]);
        setDatafluidesPurchaseOrders([]);
      }

      setValue('accessActivity', [...activitiesWithoutExclusivity, e.target.value]);
    }
  }, [formValues.accessActivity, setValue]);

  const validateEmail = value => {
    const result = FormHelper.validateEmail(value);
    return typeof result === 'string' ? result : true;
  };

  const renderStepContent = useCallback(step => {
    switch (step) {
      case 0:
        return (
          <Grid item>
            <FormElement>
              <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                  <Controller
                        control={control}
                        name="lastName"
                        render={({ field }) => (
                          <TextField
                                {...field}
                                autoComplete="new-password"
                                autoFocus
                                error={touchedFields?.lastName && !field.value || !!errors.lastName}
                                label={translate('common.lastName')}
                                name="lastName"
                                required
                            />
                        )}
                        rules={{ required: true }}
                    />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Controller
                        control={control}
                        name="firstName"
                        render={({ field }) => (
                          <TextField
                                {...field}
                                autoComplete="new-password"
                                error={touchedFields?.firstName && !field.value || !!errors.firstName}
                                label={translate('common.firstName')}
                                name="firstName"
                                required
                            />
                        )}
                        rules={{ required: true }}
                    />
                </Grid>
              </Grid>
            </FormElement>
            <FormElement>
              <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                  <Controller
                        control={control}
                        name="email"
                        render={({ field }) => (
                          <TextField
                                {...field}
                                autoComplete="new-password"
                                error={touchedFields?.email && !field.value || !!errors.email}
                                helperText={errors.email?.message}
                                label={translate('common.email')}
                                name="email"
                                required
                                type="email"
                            />
                        )}
                        rules={{
                          required: true,
                          validate: validateEmail
                        }}
                    />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Controller
                        control={control}
                        name="login"
                        render={({ field }) => (
                          <TextField
                                {...field}
                                autoComplete="new-password"
                                error={touchedFields?.login && !field.value || !!errors.login}
                                label={translate('common.login')}
                                name="login"
                                required
                            />
                        )}
                        rules={{ required: true }}
                    />
                </Grid>
              </Grid>
            </FormElement>
            <FormElement>
              <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                  <Controller
                        control={control}
                        name="phoneNumber"
                        render={({ field }) => (
                          <TextField
                                {...field}
                                autoComplete="new-password"
                                error={!!errors.phoneNumber}
                                label={translate('common.phone')}
                                name="phoneNumber"
                                type="tel"
                            />
                        )}
                    />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Controller
                        control={control}
                        name="occupation"
                        render={({ field }) => (
                          <TextField
                                {...field}
                                autoComplete="new-password"
                                error={!!errors.occupation}
                                inputProps={{ maxLength: 200 }}
                                label={translate('common.occupation')}
                                name="occupation"
                            />
                        )}
                    />
                </Grid>
              </Grid>
            </FormElement>

            {canAdministrate() && allApplicationRoles.length > 0 && (
              <FormElement label={translate('modalUserUpdate.myAccess')}>
                <Grid container spacing={2}>
                  {allApplicationRoles && allApplicationRoles.map(role => (
                    <Controller
                              control={control}
                              key={role.key}
                              name="roles"
                              render={({ field }) => (
                                <FormControlLabel
                                      control={
                                        <Radio
                                            checked={field.value === role.key}
                                            value={role.key}
                                            onChange={() => field.onChange(role.key)}
                                        />
                                      }
                                      label={role.label}
                                  />
                              )}
                          />
                      ))}
                </Grid>
                <RequiredSelectHack value={formValues.roles && formValues.roles.length > 0 ? 'ok' : ''} />
              </FormElement>
              )}

            {canAdministrate() && (
              <FormElement>
                <Grid container>
                  <Grid item style={{ flex: 1 }}>
                    <Datepicker
                            disablePast
                            fullWidth
                            label="common.validityDate"
                            style={{ flex: 1 }}
                            value={formValues.validityDate}
                            onChange={handleChangeValidityDate}
                        />
                  </Grid>
                  <Grid item>
                    <CustomTooltip text={translate('modalUser.tooltipValidityDate')} />
                  </Grid>
                </Grid>
              </FormElement>
              )}
            {/* This input is only used for forcing disabling autocomplete in chrome browser */}
            <input name="fakeInputForForcingDisablingAutocompleteChrome" style={{ display: 'none' }} type="text" />
          </Grid>
        );
      case 1:
        return (
            <Grid item>
              <FormCountryIdentifier
                  formState={{
                    values: formValues,
                    errors,
                    setField: (name, value) => setValue(name, value),
                    touched: touchedFields || {},
                    clearField: (name) => setValue(name, null)
                  }}
                  identifierFields={identifierFields}
                  raw={name => {
                    return {
                      value: formValues[name] || '',
                      onChange: value => setValue(name, value),
                      onBlur: () => trigger(name)
                    };
                  }}
                  setIdentifierFields={setIdentifierFields}
                  text={name => {
                    return {
                      value: formValues[name] || '',
                      onChange: e => setValue(name, e.target.value),
                      onBlur: () => trigger(name)
                    };
                  }}
                  withCompanyFields
              />
              <FormAddress
                  formState={{
                    values: formValues,
                    errors,
                    setField: (name, value) => setValue(name, value),
                    touched: touchedFields || {},
                    clearField: (name) => setValue(name, null)
                  }}
                  isRequired
                  text={name => {
                    return {
                      value: formValues[name] || '',
                      onChange: e => setValue(name, e.target.value),
                      onBlur: () => trigger(name)
                    };
                  }}
                  withCountry={false}
              />
              {/* This input is only used for forcing disabling autocomplete in chrome browser */}
              <input name="fakeInputForForcingDisablingAutocompleteChrome1" style={{ display: 'none' }} type="text" />
            </Grid>
        );
      case 2:
        return (
          <Grid item style={{ width: '100%' }}>
            <FormElement label={translate('inscriptionForm.selectApp')}>
              {activeApps.map(app => (
                <Fragment key={app.key}>
                  <FormControlLabel
                          control={
                            <Checkbox
                                checked={formValues.accessActivity?.includes(app.value)}
                                color="primary"
                                value={app.value}
                                onChange={checkExclusivity}
                            />
                          }
                          data-cy={`checkbox-${app.key}`}
                          key={app.key}
                          label={
                            <Grid container direction="column" style={{ paddingTop: '6px' }}>
                              <Grid alignItems="center" container item spacing={1}>
                                <Grid item>
                                  <img alt={`${app.key}_LOGO`}
                                       src={`${process.env.PUBLIC_URL}/assets/images/favicons/${app.key.split('_')[0].toLowerCase()}.ico`}
                                       style={{ width: '25px', height: '25px' }}
                                  />
                                </Grid>
                                <Grid item>
                                  <Typography style={{ fontWeight: 'bold' }}>
                                    {app.label}
                                  </Typography>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Typography>
                                  {app.tooltip}
                                </Typography>
                              </Grid>
                            </Grid>
                          }
                          style={{ alignItems: 'flex-start', paddingBottom: '20px' }}
                      />
                </Fragment>
                ))}
              {renderPurchaseOrderForms()}
            </FormElement>
          </Grid>
        );
      default:
        return null;
    }
  }, [activeApps, allApplicationRoles, canAdministrate, checkExclusivity, control,
    formValues, errors, touchedFields, handleChangeValidityDate, identifierFields, renderPurchaseOrderForms, setValue]);

  const nextStepButton = useCallback((isDisabled) => (
    <Button
          color="primary"
          data-cy="handleNextStep"
          disabled={isDisabled}
          startIcon={activeStep === LAST_STEP
              ? <FontAwesomeIcon icon={faUserPlus} /> : <FontAwesomeIcon icon={faChevronRight} />}
          variant="contained"
          onClick={handleNextStep}
      >
      {activeStep === LAST_STEP
            ? translate('button.createAccount')
            : translate('button.next')}
    </Button>
  ), [activeStep, handleNextStep]);

  return (
    <form name="registerForm" onSubmit={handleSubmit(onFormSubmit)}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {FORM_STEPS.map(step => (
          <Step key={step.key}>
            <StepLabel>{translate(step.label)}</StepLabel>
          </Step>
          ))}
      </Stepper>

      <Grid container direction="column">
        {renderStepContent(activeStep)}
      </Grid>

      <Grid container justifyContent="flex-end" spacing={2} style={{ marginTop: '3rem' }}>
        <Grid item>
          <Button
                disabled={activeStep === 0}
                startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
                onClick={handleBackStep}
            >
            {translate('button.previous')}
          </Button>
        </Grid>
        <Grid item>
          {isLoading ? (
            <Button color="primary" variant="contained">
              <FontAwesomeIcon icon={faSpinner} size="2x" spin />
            </Button>
            ) : (
              <>
                {activeStep === LAST_STEP && (!formValues.accessActivity || formValues.accessActivity.length < 1)
                      ? <Tooltip title={translate('toolTip.noActivitySelected')}>
                        <span>
                          {nextStepButton(true)}
                        </span>
                      </Tooltip>
                      : nextStepButton(false)
                  }
              </>
            )}
        </Grid>
      </Grid>
    </form>
  );
};

RegisterFormComponent.propTypes = {
  allApplicationRoles: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired
};

RegisterFormComponent.defaultProps = {
  allApplicationRoles: [],
  isLoading: false
};

const RegisterForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const displayModal = useModal();
  const navigate = useNavigate();

  const { response: allApplicationRoles } = useFetch(() => (userStore.isConnected
          ? ApplicationRolesService.getOptions()
          : []
  ), []);

  const registerUser = useCallback(formState => {
    setIsLoading(true);
    const confirmMessage = formState.accessActivity.length > 0 ? 'confirms.userList.createWithAccess' : 'confirms.userList.create';

    UserService.registerUser(formState).then(() => {
      displayModal({
        type: 'CONFIRM',
        text: translate(confirmMessage),
        onConfirm: () => navigate(ROUTES.HOME)
      });
    })
        .catch(error => displayModal({
          type: 'ERROR',
          text: error
        }))
        .finally(() => setIsLoading(false));
  }, [displayModal, navigate]);

  return (
    <Wrapper>
      <FormContainer>
        <RegisterFormComponent
              allApplicationRoles={allApplicationRoles}
              isLoading={isLoading}
              onSubmit={registerUser}
          />
      </FormContainer>
    </Wrapper>
  );
};

export default RegisterForm;
